import type { UploadFileTicket } from "@/api/datasetUpload";
import { isHttpClientError } from "@/http";
import { isAbortError } from "@/utils";
import type { ScannedFile } from "./folderScan";
import { PutFileHttpError, PutFileNetworkError, putFileToSignedUrl } from "./putFile";
import { delayUnlessAborted, throwIfUploadAborted } from "./uploadAbort";

// 파일 동시성 풀. 파일 단위 PUT 의 실행 순서와 재시도 정책의 단일 소유자다.
//
// Owns:
// - 동시 워커 수 상한. 목적지가 다른 리전이면 처리량의 상한은 대역폭이 아니라 RTT 라서, 동시성이
//   처리량을 정한다.
// - 파일당 재시도 정책:
//   - 401/403(서명 만료 시그니처): 티켓을 재발급한 뒤 1회 즉시 재시도한다.
//   - 5xx·408·429: 지수 backoff 로 시도 상한까지 재시도한다. 408·429 는 서버가 늦추라고 한 응답이다.
//   - 그 외 4xx: 즉시 실패한다.
// - 연결 판정: 연결의 증거는 PUT 의 응답과, 진행이 progressEvidenceAfterMs 넘게 이어진 PUT 의 바이트
//   증가다. 요청이 끝났는지가 아니라 바이트가 움직이는지로 판정한다. 느린 회선의 큰 파일은 수 분 동안
//   응답이 없어도 바이트는 움직인다.
//   - 브라우저는 서버에 닿지 않은 바이트도 진행으로 보고한다. 진행은 소켓에 넘긴 바이트이기 때문이다.
//     서버나 프록시가 연결을 받자마자 끊으면 첫 진행 뒤 한 번 왕복 안에 끝난다. 그래서 첫 진행 직후의
//     바이트는 증거로 세지 않는다.
// - 단절 대기: 응답 없이 끝난 요청(PUT 의 연결 오류·무진행, declare 의 연결 오류)은 기본으로 파일의
//   실패가 아니다. 시도 횟수를 쓰지 않는다. 워커가 기다렸다가 같은 요청을 다시 보낸다. 연결의 증거가
//   한도 동안 없으면 풀은 UploadConnectionLostError 로 끝난다. 그 시각에 진행 중인 요청도 중단한다.
// - 보내지 못하는 파일: 연결이 살아 있는데 한 파일만 진행을 이어 가지 못하면, 연결이 아니라 그 파일의
//   문제다. 그 파일을 실패로 둔다.
// - 거듭 끊기는 파일: 다른 파일이 완료되는 동안 한 파일만 진행 중에 maxInterruptions 번 끊기면 그 파일을
//   실패로 둔다. 회선 전체가 끊긴 동안의 끊김은 세지 않는다. 그동안은 다른 파일도 완료되지 않는다.
// - 실패 격리: 한 파일의 최종 실패가 다른 파일 업로드를 멈추지 않는다. abort 와 잡 수준 오류만
//   전체를 멈춘다. 잡 수준 오류는 declare(ticketFor) 오류처럼 남은 모든 파일에 똑같이 적용되는 오류다.
//
// Does not own:
// - 티켓 발급·만료 정책(ticketStore), 잡 상태 전이·집계(UploadEngine).

export type PutPoolConfig = {
  concurrency: number;
  maxAttempts: number;
  backoffBaseMs: number;
  // PUT 의 무진행 한도(ms). putFile 이 이 시간 동안 진행이 없는 전송을 끊는다.
  stallTimeoutMs: number;
  // 단절 대기의 상한(ms). 다음 시도까지의 대기는 backoffBaseMs 에서 두 배씩 늘고 이 값을 넘지 않는다.
  reconnectBackoffMaxMs: number;
  // 단절 판정의 유예(ms). 응답 없이 끝난 요청 뒤에 연결의 증거가 이 시간 동안 없으면 단절이다. 그
  // 안에 다른 전송의 바이트가 오면 요청 하나의 실패로 본다.
  offlineGraceMs: number;
  // 진행 증거의 지연(ms). 한 시도의 진행이 첫 진행부터 이 시간 넘게 이어진 뒤에야 그 바이트를 연결의
  // 증거로 센다. 받자마자 끊기는 연결은 첫 진행 뒤 한 번 왕복 안에 끝난다.
  progressEvidenceAfterMs: number;
  // 한 파일이 진행 중에 끊기는 횟수의 상한. 이 파일이 끊긴 사이에 다른 파일이 완료된 경우만 센다.
  maxInterruptions: number;
  // 단절 한도(ms). 마지막 연결의 증거부터 이 시간이 지나면 풀이 UploadConnectionLostError 로 끝난다.
  maxOfflineMs: number;
};

export type PutPoolCallbacks = {
  ticketFor: (file: ScannedFile) => Promise<UploadFileTicket>;
  invalidateTicket: (relativePath: string) => void;
  onFileStart: (relativePath: string) => void;
  onFileProgress: (relativePath: string, loaded: number) => void;
  onFileUploaded: (relativePath: string) => void;
  onFileFailed: (relativePath: string, error: unknown) => void;
  // 단절로 판정하면 true 를, 그 뒤에 연결의 증거가 다시 오면 false 를 전달한다.
  onConnectionChange: (waiting: boolean) => void;
};

// 단절이 한도까지 이어졌다. 파일의 실패가 아니다. 호출부는 전송을 멈추고 사용자의 재개를 기다린다.
export class UploadConnectionLostError extends Error {
  constructor() {
    super("Upload connection did not recover within the offline limit.");
    this.name = "UploadConnectionLostError";
  }
}

// 연결은 살아 있는데 이 파일의 요청만 진행을 이어 가지 못하고 응답 없이 끝났다. 바이트를 하나도 못
// 보냈거나, 연결을 받자마자 끊겼다. 연결이 아니라 파일의 문제다. 로컬 파일을 읽지 못하는 실패도 XHR
// 에서는 응답 없는 실패로 보인다.
export class UploadFileNotSentError extends Error {
  constructor() {
    super("Upload file could not keep sending while the connection was alive.");
    this.name = "UploadFileNotSentError";
  }
}

// 다른 파일은 완료되는데 이 파일만 진행 중에 응답 없이 거듭 끊겼다. 회선이 흔들리거나, 보안 장비가 이
// 파일의 업로드만 끊는 경우다. 단발 PUT 은 이어 올리지 못하므로 같은 조건이면 계속 끊긴다.
export class UploadFileInterruptedError extends Error {
  constructor() {
    super("Upload file was interrupted repeatedly while other files completed.");
    this.name = "UploadFileInterruptedError";
  }
}

// 연결 판정의 단일 소유자다. 풀의 모든 워커가 한 인스턴스를 공유한다.
//
// 마지막 연결의 증거 시각을 장부로 둔다. 판정은 모두 이 시각에서 나온다.
// - 단절: 응답 없이 끝난 요청이 마지막 증거 뒤에 있고, 마지막 증거부터 offlineGraceMs 가 지났다.
// - 마감: 마지막 증거 + maxOfflineMs. 마감 시각에 `lost` 가 reject 한다. 풀은 워커와 `lost` 를 함께
//   기다린다. 그래서 마감 시각에 진행 중인 요청도 중단된다. 그 요청들도 마감까지 바이트를 보내지
//   못했다.
// - 깨우기: PUT 의 응답은 기다리는 워커를 모두 깨운다. 바이트는 단절이 끝날 때만 깨운다. 진행 이벤트는
//   요청마다 약 50ms 간격으로 온다. 이벤트마다 깨우면 실패를 되풀이하는 파일을 그 간격으로 다시 보낸다.
//
// 진행 이벤트는 시각만 적는다. 타이머는 응답 없이 끝난 요청이 증거보다 늦을 때만 건다.
class ConnectionWatch {
  // 마감까지 증거가 없으면 UploadConnectionLostError 로 reject 한다. 그 전에는 끝나지 않는다.
  readonly lost: Promise<never>;
  private rejectLost: (error: UploadConnectionLostError) => void = () => undefined;
  // 마지막 연결의 증거 시각. 풀이 시작한 시각에서 센다.
  private lastEvidenceAt = Date.now();
  // 증거를 적을 때마다 1 씩 늘린다. 두 시점의 값을 비교해 그 사이에 증거가 있었는지 본다.
  private evidences = 0;
  // 완료한 파일 수. 두 시점의 값을 비교해 그 사이에 다른 파일이 완료되었는지 본다.
  private uploads = 0;
  // 응답 없이 끝난 요청이 마지막 증거 뒤에 있다.
  private failedSinceEvidence = false;
  // 단절로 판정했다(onChange(true) 를 보냈다).
  private offline = false;
  private judgeTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly wakers = new Set<() => void>();

  constructor(
    private readonly config: PutPoolConfig,
    private readonly onChange: (waiting: boolean) => void,
  ) {
    this.lost = new Promise<never>((_, reject) => {
      this.rejectLost = reject;
    });
  }

  // PUT 이 바이트를 더 보냈다. 단절 중이었으면 단절을 끝내고 기다리던 워커를 깨운다.
  noteBytes(): void {
    if (this.noteEvidence()) {
      this.wakeAll();
    }
  }

  // PUT 이 응답을 받았다. 상태 코드와 관계없이 연결의 증거다. 기다리던 워커를 깨운다.
  noteResponse(): void {
    this.noteEvidence();
    this.wakeAll();
  }

  // PUT 이 성공했다. 응답이므로 증거이고, 완료한 파일 수도 센다.
  noteUploaded(): void {
    this.uploads += 1;
    this.noteResponse();
  }

  // 지금까지 적은 증거의 수. 두 시점의 값이 다르면 그 사이에 증거가 있었다.
  get evidenceCount(): number {
    return this.evidences;
  }

  // 지금까지 완료한 파일 수. 두 시점의 값이 다르면 그 사이에 파일이 완료되었다.
  get uploadedCount(): number {
    return this.uploads;
  }

  // 응답 없이 끝난 요청을 적고 다음 시도까지 기다린다. `retries` 는 이 워커가 이 요청을 이미 다시 보낸
  // 횟수다(0부터). 마감이 지났으면 UploadConnectionLostError 를 던진다. abort 되면 AbortError 를 던진다.
  async waitToRetry(retries: number, signal: AbortSignal): Promise<void> {
    // 이미 abort 된 signal 은 abort 이벤트를 다시 내지 않는다. 그 signal 로 잠들면 대기가 끝날 때까지
    // 깨어나지 않는다. declare 요청은 중단할 수 없어서, 그 요청이 도는 동안 abort 가 올 수 있다.
    throwIfUploadAborted(signal);
    this.noteNoResponse();
    this.throwIfPastDeadline();
    const backoffMs = Math.min(
      this.config.reconnectBackoffMaxMs,
      this.config.backoffBaseMs * 2 ** retries,
    );
    await this.sleep(backoffMs, signal);
    // 대기가 끝난 뒤에 마감을 다시 본다. 마감 시각부터는 새 요청을 시작하지 않는다.
    this.throwIfPastDeadline();
  }

  // 풀이 끝날 때 호출한다. 판정 타이머를 지운다. 그 뒤에는 `lost` 가 reject 하지 않는다.
  dispose(): void {
    this.clearJudgeTimer();
  }

  // 증거를 적는다. 단절 중이었으면 단절을 끝내고 true 를 반환한다.
  private noteEvidence(): boolean {
    this.lastEvidenceAt = Date.now();
    this.evidences += 1;
    this.failedSinceEvidence = false;
    this.clearJudgeTimer();
    if (!this.offline) {
      return false;
    }
    this.offline = false;
    this.onChange(false);
    return true;
  }

  // 마지막 증거 뒤의 첫 실패만 판정 타이머를 건다. 그 뒤의 실패는 이미 걸린 타이머가 판정한다.
  private noteNoResponse(): void {
    if (this.failedSinceEvidence) {
      return;
    }
    this.failedSinceEvidence = true;
    this.scheduleJudge();
  }

  // 다음 판정 시각에 타이머를 건다. 단절 전에는 유예가 끝나는 시각이고, 단절 뒤에는 마감 시각이다.
  // 타이머는 마지막 증거 뒤에 실패가 있을 때만 걸려 있다. 그래서 여기에 오면 걸린 타이머가 없다.
  private scheduleJudge(): void {
    const deadlineAt = this.lastEvidenceAt + this.config.maxOfflineMs;
    const nextAt = this.offline
      ? deadlineAt
      : Math.min(this.lastEvidenceAt + this.config.offlineGraceMs, deadlineAt);
    this.judgeTimer = setTimeout(() => this.judge(), Math.max(0, nextAt - Date.now()));
  }

  // 증거가 오면 타이머를 지운다. 그래서 이 판정은 마지막 증거 뒤에 실패만 있을 때 실행된다.
  private judge(): void {
    this.judgeTimer = null;
    if (this.isPastDeadline()) {
      this.rejectLost(new UploadConnectionLostError());
      return;
    }
    if (!this.offline && Date.now() - this.lastEvidenceAt >= this.config.offlineGraceMs) {
      this.offline = true;
      this.onChange(true);
    }
    this.scheduleJudge();
  }

  private throwIfPastDeadline(): void {
    if (this.isPastDeadline()) {
      throw new UploadConnectionLostError();
    }
  }

  // 마지막 증거 뒤에 실패만 있고, 마지막 증거부터 maxOfflineMs 가 지났다.
  private isPastDeadline(): boolean {
    return this.failedSinceEvidence && Date.now() - this.lastEvidenceAt >= this.config.maxOfflineMs;
  }

  private clearJudgeTimer(): void {
    if (this.judgeTimer !== null) {
      clearTimeout(this.judgeTimer);
      this.judgeTimer = null;
    }
  }

  private wakeAll(): void {
    for (const wake of [...this.wakers]) {
      wake();
    }
  }

  // 시간이 지나거나, 깨우거나, abort 되면 깨어난다.
  private sleep(ms: number, signal: AbortSignal): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const finish = (settle: () => void): void => {
        clearTimeout(timer);
        signal.removeEventListener("abort", onAbort);
        this.wakers.delete(wake);
        settle();
      };
      const wake = (): void => finish(resolve);
      const onAbort = (): void =>
        finish(() => reject(new DOMException("Upload aborted", "AbortError")));
      const timer = setTimeout(wake, ms);
      this.wakers.add(wake);
      signal.addEventListener("abort", onAbort, { once: true });
    });
  }
}

// 파일 목록을 동시성 풀로 업로드한다. abort 되면 AbortError 를 던진다(진행 중 XHR 도 중단한다).
// 그 외에는 개별 실패를 onFileFailed 로 전달하고 끝까지 처리한다.
//
// 워커 하나가 잡 수준 오류로 끝나면 풀은 그 오류를 던진다. 그 전에 나머지 워커를 중단한다. 풀이
// 끝난 뒤에 PUT 이 나가면, 호출부가 같은 파일을 다시 올릴 때 전송이 두 개가 된다.
export async function runPutPool(
  files: readonly ScannedFile[],
  config: PutPoolConfig,
  callbacks: PutPoolCallbacks,
  signal: AbortSignal,
): Promise<void> {
  // 호출부의 abort 와 풀 자신의 중단을 한 signal 로 묶는다. 워커는 이 signal 만 본다.
  const pool = new AbortController();
  const abortPool = (): void => pool.abort();
  if (signal.aborted) {
    abortPool();
  }
  signal.addEventListener("abort", abortPool, { once: true });

  const watch = new ConnectionWatch(config, callbacks.onConnectionChange);
  let nextIndex = 0;
  const workerCount = Math.max(1, Math.min(config.concurrency, files.length));
  const workers = Array.from({ length: workerCount }, async () => {
    for (;;) {
      throwIfUploadAborted(pool.signal);
      const index = nextIndex;
      nextIndex += 1;
      if (index >= files.length) {
        return;
      }
      await uploadWithRetry(files[index], config, callbacks, watch, pool.signal);
    }
  });
  try {
    // 단절의 마감(`watch.lost`)도 함께 기다린다. 마감 시각에 바이트를 보내지 못한 요청이 남아 있어도
    // 풀이 끝난다.
    await Promise.race([Promise.all(workers), watch.lost]);
  } catch (error) {
    abortPool();
    throw error;
  } finally {
    watch.dispose();
    signal.removeEventListener("abort", abortPool);
  }
}

async function uploadWithRetry(
  file: ScannedFile,
  config: PutPoolConfig,
  callbacks: PutPoolCallbacks,
  watch: ConnectionWatch,
  signal: AbortSignal,
): Promise<void> {
  callbacks.onFileStart(file.relativePath);
  let attempts = 0;
  // 이 파일의 PUT 이 응답 없이 끝난 뒤 다시 보낸 횟수. 다음 대기의 길이를 정한다.
  let reconnects = 0;
  // 연결이 살아 있는 동안 진행을 이어 가지 못하고 응답 없이 끝난 연속 횟수.
  let unsentStreak = 0;
  // 직전에 응답 없이 끝난 시점의 증거 표식. 그 뒤에 연결의 증거가 있었는지 본다.
  let lastFailureMark: number | null = null;
  // 다른 파일이 완료되는 동안 진행 중에 끊긴 연속 횟수와, 직전에 끊긴 시점의 완료 수.
  let interruptions = 0;
  let lastInterruptionMark: number | null = null;
  // 만료 시그니처(401/403) 재발급은 backoff 시도와 별도로 1회만 허용한다. 반복되면 실제 거부다.
  let reissued = false;
  for (;;) {
    attempts += 1;
    const ticket = await ticketWithReconnect(file, callbacks, watch, signal);
    throwIfUploadAborted(signal);
    // 이 시도가 보낸 바이트와 첫 진행 시각. 재시도는 0 부터 다시 센다. 그래서 파일이 아니라 시도 단위로 둔다.
    let sentBytes = 0;
    let firstProgressAt: number | null = null;
    // 진행이 progressEvidenceAfterMs 넘게 이어졌다. 그 뒤의 바이트는 서버가 실제로 받고 있다는 증거다.
    let keptSending = false;
    try {
      await putFileToSignedUrl(ticket.upload_url, file.file, {
        signal,
        // 티켓의 헤더는 그대로 보낸다. staging 위치마다 필요한 헤더가 다르다.
        headers: ticket.upload_headers,
        stallTimeoutMs: config.stallTimeoutMs,
        onProgress: (loaded) => {
          if (loaded > sentBytes) {
            sentBytes = loaded;
            const now = Date.now();
            firstProgressAt ??= now;
            if (now - firstProgressAt >= config.progressEvidenceAfterMs) {
              keptSending = true;
              watch.noteBytes();
            }
          }
          callbacks.onFileProgress(file.relativePath, loaded);
        },
      });
      watch.noteUploaded();
      callbacks.onFileUploaded(file.relativePath);
      return;
    } catch (error) {
      if (isAbortError(error) || signal.aborted) {
        throw error;
      }
      if (isNetworkFailure(error)) {
        // 응답이 없다. 기본으로 연결의 문제다. 시도 횟수를 쓰지 않고, 기다렸다가 다시 보낸다.
        attempts -= 1;
        const aliveSinceLastFailure =
          lastFailureMark !== null && watch.evidenceCount !== lastFailureMark;
        lastFailureMark = watch.evidenceCount;
        if (keptSending) {
          // 이 시도는 진행을 이어 갔다. 파일은 읽을 수 있고 연결은 방금까지 살아 있었다. 그래서 횟수와
          // 대기를 처음 값부터 다시 센다.
          unsentStreak = 0;
          reconnects = 0;
          // 진행 중에 끊겼다. 그 사이에 다른 파일이 완료되었으면 회선 전체의 단절이 아니다. 그런 끊김이
          // 이어지면 이 파일의 문제다.
          const othersUploaded =
            lastInterruptionMark !== null && watch.uploadedCount !== lastInterruptionMark;
          interruptions = othersUploaded ? interruptions + 1 : 1;
          lastInterruptionMark = watch.uploadedCount;
          if (othersUploaded && interruptions >= config.maxInterruptions) {
            callbacks.onFileFailed(file.relativePath, new UploadFileInterruptedError());
            return;
          }
        } else {
          // 이 파일만 진행을 잇지 못하고 다른 전송은 증거를 낸다. 그 실패가 이어지면 파일의 문제다.
          unsentStreak = aliveSinceLastFailure ? unsentStreak + 1 : 1;
        }
        if (aliveSinceLastFailure && unsentStreak >= config.maxAttempts) {
          callbacks.onFileFailed(file.relativePath, new UploadFileNotSentError());
          return;
        }
        await watch.waitToRetry(reconnects, signal);
        reconnects += 1;
        continue;
      }
      // 서버가 응답했다. 연결은 살아 있다.
      watch.noteResponse();
      reconnects = 0;
      unsentStreak = 0;
      lastFailureMark = null;
      interruptions = 0;
      lastInterruptionMark = null;
      if (isExpiredSignature(error) && !reissued) {
        reissued = true;
        attempts -= 1;
        callbacks.invalidateTicket(file.relativePath);
        continue;
      }
      if (isRetryableTransfer(error) && attempts < config.maxAttempts) {
        await backoffDelay(config.backoffBaseMs, attempts, signal);
        continue;
      }
      callbacks.onFileFailed(file.relativePath, error);
      return;
    }
  }
}

// 티켓을 받는다. declare 가 응답 없이 끝나면 PUT 과 같은 단절 대기를 탄 뒤 다시 선언한다.
//
// 그 밖의 ticketFor(declare 경유) 오류는 파일 격리 대상이 아니다. lease 409, 상한 413 같은 registry
// 계약 오류는 이후 모든 파일에 똑같이 적용된다. 그래서 풀을 멈추고 잡 수준으로 전파한다.
async function ticketWithReconnect(
  file: ScannedFile,
  callbacks: PutPoolCallbacks,
  watch: ConnectionWatch,
  signal: AbortSignal,
): Promise<UploadFileTicket> {
  for (let retries = 0; ; retries += 1) {
    try {
      return await callbacks.ticketFor(file);
    } catch (error) {
      if (!isNetworkFailure(error)) {
        throw error;
      }
      await watch.waitToRetry(retries, signal);
    }
  }
}

// HTTP 응답 없이 끝난 요청인지 판정한다. PUT 의 연결 오류·무진행과 declare 의 연결 오류·시간 초과가
// 여기에 든다.
function isNetworkFailure(error: unknown): boolean {
  if (error instanceof PutFileNetworkError) {
    return true;
  }
  return isHttpClientError(error) && (error.kind === "network" || error.kind === "timeout");
}

function isExpiredSignature(error: unknown): boolean {
  return error instanceof PutFileHttpError && (error.status === 401 || error.status === 403);
}

// 5xx·408·429 와 분류하지 못한 오류는 일시 오류로 보고 backoff 재시도한다. GCS 는 408·429 를 5xx 와
// 같이 다시 보낼 응답으로 안내한다. 응답 없이 끝난 요청은 여기에 오지 않는다. 그 요청은 단절 대기가
// 먼저 받는다.
function isRetryableTransfer(error: unknown): boolean {
  if (error instanceof PutFileHttpError) {
    return error.status >= 500 || error.status === 408 || error.status === 429;
  }
  return error instanceof Error;
}

async function backoffDelay(baseMs: number, attempt: number, signal: AbortSignal): Promise<void> {
  // 지수 backoff + jitter(0~25%). abort 되면 즉시 깨어나 AbortError 를 던진다.
  const delay = baseMs * 2 ** (attempt - 1) * (1 + Math.random() * 0.25);
  await delayUnlessAborted(delay, signal);
}
